import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { ArrowRight } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslationApp } from "../../../lib/i18next";
import {
  HOME_FEATURE_UNUSED_BADGE,
  HOME_FEATURE_USAGE_COUNT,
  HOME_FEATURE_USAGE_COUNT_SINGULAR,
} from "../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../utils/constants";
import {
  BOX_SHADOW_BUTTON_CARD,
  GRADIENT_BLACK_COLOR,
  INPUT_COLOR_BORDER,
  WHITE_COLOR,
} from "../../../utils/colors";

const FeatureCardIcon = ({ feature }) => {
  const Icon = feature.icon;

  return (
    <Box
      aria-hidden="true"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 48,
        height: 48,
        borderRadius: 4,
        backgroundImage: GRADIENT_BLACK_COLOR,
        color: WHITE_COLOR,
        boxShadow: BOX_SHADOW_BUTTON_CARD,
      }}
    >
      <Icon size={24} strokeWidth={2} color={WHITE_COLOR} />
    </Box>
  );
};

const actionAreaSx = {
  p: 2.5,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "space-between",
  "&:hover .MuiCardActionArea-focusHighlight": {
    opacity: 0,
  },
};

const FeatureCardAction = ({ action, children }) => {
  switch (action.type) {
    case "navigate":
      return (
        <CardActionArea component={RouterLink} to={action.to} sx={actionAreaSx}>
          {children}
        </CardActionArea>
      );
    case "run":
      return (
        <CardActionArea onClick={action.onClick} sx={actionAreaSx}>
          {children}
        </CardActionArea>
      );
    case "none":
      return (
        <Box
          sx={{
            p: 2.5,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {children}
        </Box>
      );
    default: {
      const _exhaustiveCheck = action;
      void _exhaustiveCheck;
      return null;
    }
  }
};

const FeatureCard = ({
  feature,
  action = { type: "none" },
  isDiscoverSection = false,
}) => {
  const { t } = useTranslationApp();
  const hasUsage = feature.count > 0;
  const hasAction = action.type !== "none";

  const cardContent = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <FeatureCardIcon feature={feature} />

        {hasUsage ? (
          <Chip
            size="small"
            label={
              feature.count === 1
                ? t(HOME_FEATURE_USAGE_COUNT_SINGULAR)
                : t(HOME_FEATURE_USAGE_COUNT, { count: feature.count })
            }
            sx={{
              bgcolor: "action.hover",
              color: "text.secondary",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: 26,
            }}
          />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={t(HOME_FEATURE_UNUSED_BADGE)}
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: 26,
              borderColor: INPUT_COLOR_BORDER,
            }}
          />
        )}
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(feature.titleKey)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, lineHeight: 1.6 }}
        >
          {t(feature.descriptionKey)}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "text.primary",
        }}
      >
        {t(feature.ctaKey)}
        <ArrowRight
          size={16}
          aria-hidden="true"
          style={{
            transition: "transform 0.2s ease",
          }}
          className="feature-card-arrow"
        />
      </Box>
    </>
  );

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderStyle: isDiscoverSection ? "dashed" : "solid",
        borderRadius: BORDER_RADIUS,
        bgcolor: "background.paper",
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        ...(hasAction && {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 2,
            "& .feature-card-arrow": {
              transform: "translateX(2px)",
            },
          },
        }),
      }}
    >
      <FeatureCardAction action={action}>{cardContent}</FeatureCardAction>
    </Card>
  );
};

export default FeatureCard;
